/**
 * Header behaviour: the header that returns on the way up, and the mega menu, a custom element so it also works when
 * the theme editor re-renders the header. The menu panel on phones is a
 * <side-drawer>, see drawer.js.
 */
class MegaMenu extends HTMLElement {
  #controller = new AbortController();
  #toggle;

  connectedCallback() {
    const { signal } = this.#controller;
    this.#toggle = this.querySelector('.sda-nav__toggle');

    // A mouse has already opened the panel by hovering, so its click must not close it again.
    this.#toggle.addEventListener('click', (event) => (this.open = event.pointerType === 'mouse' || !this.open), { signal });
    this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && (this.open = true), { signal });
    this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && (this.open = false), { signal });
    this.addEventListener('focusout', (event) => this.contains(event.relatedTarget) || (this.open = false), { signal });
    this.addEventListener('keydown', (event) => event.key === 'Escape' && this.#dismiss(), { signal });
    document.addEventListener('click', (event) => this.contains(event.target) || (this.open = false), { signal });

    for (const shelf of this.querySelectorAll('.sda-mega__main')) this.#bindShelf(shelf, signal);
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  get open() {
    return this.classList.contains('is-open');
  }

  set open(value) {
    this.classList.toggle('is-open', value);
    this.#toggle.setAttribute('aria-expanded', String(value));
  }

  #dismiss() {
    if (!this.open) return;
    this.open = false;
    this.#toggle.focus();
  }

  #bindShelf(shelf, signal) {
    let timer;
    const activate = () => {
      for (const other of this.querySelectorAll('.sda-mega__main.is-active')) other.classList.remove('is-active');
      shelf.classList.add('is-active');
    };

    // The short delay keeps a diagonal move towards the sub-shelves from switching shelf.
    shelf.addEventListener('pointerenter', () => (timer = setTimeout(activate, 90)), { signal });
    shelf.addEventListener('pointerleave', () => clearTimeout(timer), { signal });
    shelf.addEventListener('focusin', activate, { signal });
    shelf.querySelector('.sda-mega__row').addEventListener(
      'click',
      (event) => {
        if (shelf.classList.contains('is-active')) return;
        event.preventDefault();
        activate();
      },
      { signal }
    );
  }
}

/**
 * The header slides away on the way down and comes back on the way up. While
 * it is in view, `--header-offset` tells everything else that sticks to the
 * top of the screen how much room it takes. It stays put while the menu or
 * the search has the focus.
 */
const header = document.querySelector('.sda-header-section');
if (header) {
  let last = window.scrollY;

  const update = () => {
    const y = window.scrollY;
    if (Math.abs(y - last) < 6) return;

    // A stuck header reports where it sticks, so its own place is read from what stands above it.
    const above = header.previousElementSibling;
    const start = above ? above.offsetTop + above.offsetHeight : 0;
    const past = y > start + header.offsetHeight;
    const busy = header.matches(':focus-within') || header.querySelector('mega-menu[open], .sda-nav__item.is-open');
    const hidden = y > last && past && !busy;
    header.classList.toggle('is-hidden', hidden);
    header.classList.toggle('is-stuck', past && !hidden);
    document.documentElement.style.setProperty('--header-offset', past && !hidden ? `${header.offsetHeight}px` : '0px');
    last = y;
  };
  window.addEventListener('scroll', update, { passive: true });
}

// Keeps the basket counter in step with the cart; see cart-api.js for the event.
document.addEventListener('cart:updated', ({ detail: { cart } }) => {
  for (const bubble of document.querySelectorAll('[data-cart-count]')) {
    bubble.querySelector('[data-cart-count-number]').textContent = cart.item_count;
    bubble.hidden = cart.item_count === 0;
    bubble.classList.remove('is-bump');
    void bubble.offsetWidth;
    bubble.classList.add('is-bump');
  }
});

customElements.define('mega-menu', MegaMenu);
